/**
 * ModernSettingsScreen Component
 * Modern profile interface with menu items
 */

import { useState, useEffect } from 'react';
import { useUserContext } from '../contexts';
import { useTranslation } from '../hooks/useTranslation';
import { LanguageModal } from '../components/LanguageModal';

type SettingsItem = {
  id: string;
  icon: string;
  titleKey: string;
  subtitle?: string;
  gradient: string;
  action: () => void;
};

export function ModernSettingsScreen({ onNavigateToAdmin, onNavigateToReferral }: { onNavigateToAdmin?: () => void; onNavigateToReferral?: () => void }) {
  const { user, refreshUser } = useUserContext();
  const { t, language } = useTranslation();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  // Force refresh user data when component mounts
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const getLanguageDisplayName = (lang: string) => {
    switch (lang) {
      case 'en': return 'English 🇺🇸';
      case 'ru': return 'Русский 🇷🇺';
      case 'tr': return 'Türkçe 🇹🇷';
      default: return 'English 🇺🇸';
    }
  };

  const settingsItems: SettingsItem[] = [
    {
      id: 'referral',
      icon: '👥',
      titleKey: 'referral.title',
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
      action: () => onNavigateToReferral?.(),
    },
    {
      id: 'language',
      icon: '🌐',
      titleKey: 'user.language',
      subtitle: getLanguageDisplayName(language),
      gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      action: () => setIsLanguageModalOpen(true),
    },
    {
      id: 'support',
      icon: '❓',
      titleKey: 'settings.support',
      gradient: 'linear-gradient(135deg, #065f46 0%, #059669 100%)',
      action: () => {
        // Open discussion/chat section of the channel
        if (window.Telegram?.WebApp?.openTelegramLink) {
          window.Telegram.WebApp.openTelegramLink('https://t.me/WintradeVipWiton?chat');
        } else {
          window.open('https://t.me/WintradeVipWiton?chat', '_blank');
        }
      },
    },
    {
      id: 'privacy',
      icon: '🛡️',
      titleKey: 'settings.privacy',
      gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      action: () => window.open('https://wintrade.cfd/privacy.html', '_blank'),
    },
    {
      id: 'terms',
      icon: '📄',
      titleKey: 'settings.terms',
      gradient: 'linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 100%)',
      action: () => window.open('https://wintrade.cfd/terms.html', '_blank'),
    },
  ];

  // Add admin button if user is admin
  if (user?.isAdmin && onNavigateToAdmin) {
    settingsItems.push({
      id: 'admin',
      icon: '🛠️',
      titleKey: 'settings.admin',
      gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      action: onNavigateToAdmin,
    });
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        padding: '28px 20px 20px',
        textAlign: 'center',
        background: 'rgba(10, 22, 40, 0.8)',
        backdropFilter: 'blur(10px)',
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          margin: 0,
        }}>
          {t('settings.title')}
        </h1>
      </div>

      {/* User Profile Card */}
      {user && (
        <div style={{
          margin: '16px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          {/* Avatar */}
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: 700,
            border: '3px solid rgba(255, 255, 255, 0.3)',
          }}>
            {user.firstName.charAt(0).toUpperCase()}
          </div>

          {/* User Info */}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              marginBottom: '4px',
            }}>
              {user.firstName} {user.lastName || ''}
            </div>
            {user.username && (
              <div style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.8)',
                marginBottom: '4px',
              }}>
                @{user.username}
              </div>
            )}
            <div style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 600,
            }}>
              {t('user.level')} {user.level} • {user.balance.toLocaleString()} WiTon
            </div>
          </div>
        </div>
      )}

      {/* Settings Items */}
      <div style={{ padding: '0 16px' }}>
        {settingsItems.map((item) => (
          <button
            key={item.id}
            onClick={item.action}
            style={{
              width: '100%',
              background: 'rgba(10, 22, 40, 0.6)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '12px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(10, 22, 40, 0.8)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(10, 22, 40, 0.6)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* Icon */}
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: item.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              flexShrink: 0,
            }}>
              {item.icon}
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              textAlign: 'left',
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: item.subtitle ? '4px' : 0,
                color: '#fff',
              }}>
                {t(item.titleKey)}
              </div>
              {item.subtitle && (
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.6)',
                }}>
                  {item.subtitle}
                </div>
              )}
            </div>

            {/* Arrow */}
            <div style={{
              fontSize: '20px',
              color: 'rgba(255, 255, 255, 0.5)',
            }}>
              →
            </div>
          </button>
        ))}
      </div>

      {/* App Version */}
      <div style={{
        margin: '24px 16px 16px',
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.4)',
        fontSize: '12px',
      }}>
        <div style={{ marginBottom: '4px' }}>WinTrade Trading Simulator</div>
        <div>Version 1.0.0</div>
      </div>

      {/* Language Modal */}
      <LanguageModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        currentLanguage={language}
      />
    </div>
  );
}
